import { Bell, ShoppingCart, Gift, Settings, Menu, Moon, Sun, Building2, ChevronDown, Check } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useState, useRef, useEffect, useCallback } from 'react';
import { GlobalSearch } from '../shared/GlobalSearch';
import { BranchSelector } from '../shared/BranchSelector';
import { DEFAULT_AVATAR_URL } from '../../constants/avatar';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../hooks/useNotifications';
import { notificationsService, Notification as ApiNotification } from '../../services/notifications.service';
import { promotionsService, Promotion as ApiPromotion } from '../../services/promotions.service';
import { tenantsService, TenantSummary } from '../../services/tenants.service';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { toast } from 'sonner';

interface TopBarProps {
  onMenuClick: () => void;
  sidebarOpen?: boolean;
  onNavigate?: (page: string) => void;
  onOrderClick?: (orderId: string) => void;
  onCustomerClick?: (customerId: string) => void;
  onCategoryClick?: (categoryId: string) => void;
}

export function TopBar({ onMenuClick, sidebarOpen, onNavigate, onOrderClick, onCustomerClick, onCategoryClick }: TopBarProps) {
  const { getTotalItems } = useCart();
  const { user, switchActiveTenant, activeTenantId } = useAuth();
  const { theme, setTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPromotions, setShowPromotions] = useState(false);
  const [showTenantSwitcher, setShowTenantSwitcher] = useState(false);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [pendingTenant, setPendingTenant] = useState<TenantSummary | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const promotionRef = useRef<HTMLDivElement>(null);
  const [promotions, setPromotions] = useState<ApiPromotion[]>([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const {
    notifications,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications({ page: 1, limit: 10 });

  const toUiType = (type: ApiNotification['type']) => {
    switch (type) {
      case 'ORDER':
        return 'order';
      case 'INVENTORY':
        return 'alert';
      case 'PROMOTION':
        return 'promotion';
      default:
        return 'alert';
    }
  };

  const formatRelativeTime = (createdAt: string) => {
    const now = Date.now();
    const then = new Date(createdAt).getTime();
    const diffMinutes = Math.max(1, Math.floor((now - then) / (1000 * 60)));

    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  const notificationItems = notifications.map((n) => {
    const data = (n as any).data || {};
    const requestType = data.type as string | undefined;
    const approvalResolved = !!(data.approvalStatus);
    const isStatusChangeRequest =
      !approvalResolved &&
      (requestType === 'ORDER_STATUS_CHANGE_REQUEST' ||
        requestType === 'REJECTION_REQUEST' ||
        (n.title || '').toLowerCase().includes('approval required'));
    return {
      id: n.id,
      type: toUiType(n.type),
      title: n.title || '',
      message: n.message,
      time: formatRelativeTime(n.createdAt),
      unread: !n.isRead,
      orderId: data.orderId as string | undefined,
      requestedStatus: (data.requestedStatus || (requestType === 'REJECTION_REQUEST' ? 'REJECTED' : undefined)) as string | undefined,
      requestedByName: data.requestedByName as string | undefined,
      isStatusChangeRequest,
      approvalResolved,
    };
  });

  const unreadNotifications = notificationItems.filter((n) => n.unread).length;
  const newOrderCount = notifications.filter((n) => n.type === 'ORDER' && !n.isRead).length;
  const hasSettingsAccess = Boolean(user?.permissions?.includes('settings'));
  const canSwitchTenants =
    (user?.role || '').toLowerCase() === 'super_admin' ||
    Boolean(user?.permissions?.includes('tenants_platform'));
  const selectedTenantId =
    (canSwitchTenants ? activeTenantId : null) ||
    user?.tenantId ||
    user?.restaurantId ||
    '';
  const selectedTenantName =
    tenants.find((tenant) => tenant.id === selectedTenantId)?.name ||
    localStorage.getItem('activeTenantName') ||
    'Select Tenant';

  const getExpiresIn = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffMs = end.getTime() - now.getTime();

    if (diffMs <= 0) return 'expired';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'}`;
    }

    const hours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)));
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  };

  const activePromotions = promotions.filter((promotion) => {
    const now = new Date();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);

    return (
      promotion.isActive &&
      promotion.status === 'ACTIVE' &&
      start <= now &&
      end >= now
    );
  });

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await promotionsService.getPromotions({ page: 1, limit: 20 });
      const payload: any = response.data;
      const rows: ApiPromotion[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setPromotions(rows);
    } catch {
      // Keep TopBar resilient: silently fail and keep prior state.
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    if (!canSwitchTenants) return;

    try {
      const response = await tenantsService.getTenants({ page: 1, limit: 200 });
      const payload: any = response.data;
      const rows: TenantSummary[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : [];
      setTenants(rows);
    } catch {
      // Keep top bar resilient.
    }
  }, [canSwitchTenants]);

  const handleClearNotifications = async () => {
    try {
      await notificationsService.deleteAllRead();
      await refresh();
      toast.success('Notifications cleared');
    } catch {
      toast.error('Failed to clear notifications');
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (promotionRef.current && !promotionRef.current.contains(event.target as Node)) {
        setShowPromotions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchTenants();

    const timer = setInterval(() => {
      refresh();
      fetchPromotions();
    }, 15000);

    return () => clearInterval(timer);
  }, [refresh, fetchPromotions, fetchTenants]);

  const handleTenantSelection = (tenantId: string) => {
    if (!tenantId || tenantId === selectedTenantId) {
      return;
    }

    const targetTenant = tenants.find((tenant) => tenant.id === tenantId) || null;
    if (!targetTenant) {
      return;
    }

    setPendingTenant(targetTenant);
  };

  const confirmTenantSwitch = () => {
    if (!pendingTenant) {
      return;
    }
    // switchActiveTenant updates activeTenantId state in AuthContext.
    // AuthGate in App.tsx keys AppContent on activeTenantId, so React unmounts and remounts
    // the entire app tree cleanly — all hooks and contexts reinitialize for the new tenant
    // without any window.location.reload() race conditions.
    switchActiveTenant(pendingTenant.id, pendingTenant.name);
    toast.success(`Switched to ${pendingTenant.name}`);
    setPendingTenant(null);
  };

  return (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-6 py-4 h-[73px] flex items-center">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between w-full">
        <div className="flex items-center gap-4 flex-1">
          {/* Menu toggle button - only show when sidebar is closed */}
          <div className="flex items-center gap-3 flex-1">
            {!sidebarOpen && (
              <button
                onClick={onMenuClick}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Open sidebar"
              >
                <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}
            
            <GlobalSearch 
              onNavigate={onNavigate}
              onOrderClick={onOrderClick}
              onCustomerClick={onCustomerClick}
              onCategoryClick={onCategoryClick}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 ml-3">
          {canSwitchTenants && (
            <DropdownMenu open={showTenantSwitcher} onOpenChange={setShowTenantSwitcher}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="hidden md:flex items-center gap-2 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <Building2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedTenantName}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Select Tenant
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    onClick={() => {
                      handleTenantSelection(tenant.id);
                      setShowTenantSwitcher(false);
                    }}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full gap-3">
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">{tenant.name}</span>
                        {tenant.address && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {tenant.address}
                          </span>
                        )}
                      </div>
                      {selectedTenantId === tenant.id && (
                        <Check className="w-4 h-4 text-indigo-600" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Branch Selector */}
          <BranchSelector />

          <div className="hidden lg:flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-6 mr-3">
              <button onClick={() => onNavigate?.('about')} className="text-sm text-gray-900 dark:text-gray-300 font-medium hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap">About</button>
              <button onClick={() => onNavigate?.('tasks')} className="text-sm text-gray-900 dark:text-gray-300 font-medium hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap">Tasks</button>
              <button onClick={() => onNavigate?.('help')} className="text-sm text-gray-900 dark:text-gray-300 font-medium hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap">Help</button>
            </div>

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) {
                    refresh();
                  }
                }}
                className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {newOrderCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {newOrderCount > 99 ? '99+' : newOrderCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 shadow-xl rounded-xl w-80 z-50">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      {notificationItems.length > 0 && (
                        <button
                          type="button"
                          onClick={handleClearNotifications}
                          className="text-xs font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {unreadNotifications > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {unreadNotifications} unread
                      </p>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notificationItems.length === 0 && (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No notifications</div>
                    )}
                    {notificationItems.map((notification) => {
                      const isActionable = notification.isStatusChangeRequest && isAdminOrManager;
                      const canNavigate = !!notification.orderId && !!onOrderClick;

                      const handleClick = () => {
                        if (!notification.orderId || !onOrderClick) return;
                        if (notification.isStatusChangeRequest) {
                          const rawNotif = notifications.find((n) => n.id === notification.id);
                          const pending = JSON.parse(localStorage.getItem('qrypt_pending_status_changes') || '{}');
                          pending[notification.orderId] = {
                            orderNumber: (rawNotif as any)?.data?.orderNumber || notification.orderId,
                            requestedStatus: notification.requestedStatus || 'REJECTED',
                            requestedByName: notification.requestedByName || 'Someone',
                            notificationId: notification.id,
                            requestedAt: (rawNotif as any)?.createdAt || new Date().toISOString(),
                          };
                          localStorage.setItem('qrypt_pending_status_changes', JSON.stringify(pending));
                        } else if (notification.unread) {
                          markAsRead(notification.id).catch(() => {});
                        }
                        setShowNotifications(false);
                        onOrderClick(notification.orderId);
                      };

                      return (
                      <div
                        key={notification.id}
                        onClick={canNavigate ? handleClick : undefined}
                        className={`p-3 border-b border-gray-100 dark:border-gray-700/40 last:border-0 transition-colors ${
                          isActionable
                            ? 'bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/15 cursor-pointer'
                            : notification.unread
                            ? 'bg-indigo-50/80 dark:bg-indigo-500/10 hover:bg-indigo-100/80 dark:hover:bg-indigo-500/15 ' + (canNavigate ? 'cursor-pointer' : '')
                            : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/60 ' + (canNavigate ? 'cursor-pointer' : '')
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${
                            isActionable ? 'bg-amber-100 dark:bg-amber-500/20' :
                            notification.type === 'order' ? 'bg-blue-100 dark:bg-blue-500/20' :
                            notification.type === 'review' ? 'bg-green-100 dark:bg-green-500/20' :
                            'bg-orange-100 dark:bg-orange-500/20'
                          }`}>
                            {isActionable ? (
                              <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            ) : notification.type === 'order' ? (
                              <ShoppingCart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            ) : notification.type === 'review' ? (
                              <Gift className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <Bell className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm text-gray-900 dark:text-gray-100 leading-snug">
                                {notification.message}
                              </p>
                              {isActionable && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-200 dark:bg-amber-500/30 text-amber-800 dark:text-amber-300 whitespace-nowrap">
                                  Action Required
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {notification.time}
                            </p>
                            {notification.unread && !notification.isStatusChangeRequest && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  markAsRead(notification.id).catch(() => toast.error('Failed to mark notification as read'));
                                }}
                                className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 mt-1"
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700/60">
                    <button 
                      onClick={() => {
                        setShowNotifications(false);
                        onNavigate?.('notifications');
                      }}
                      className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300"
                    >
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => onNavigate?.('cart')}
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              <ShoppingCart className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getTotalItems()}
                </span>
              )}
            </button>
            
            {/* Promotions */}
            <div className="relative" ref={promotionRef}>
              <button
                onClick={() => {
                  setShowPromotions(!showPromotions);
                  if (!showPromotions) {
                    fetchPromotions();
                  }
                }}
                className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <Gift className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {activePromotions.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {activePromotions.length > 99 ? '99+' : activePromotions.length}
                  </span>
                )}
              </button>

              {/* Promotions Dropdown */}
              {showPromotions && (
                <div className="absolute right-0 top-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-xl w-80 z-50">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active Promotions</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {activePromotions.length} active offers
                    </p>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {activePromotions.length === 0 && (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No active promotions</div>
                    )}
                    {activePromotions.map((promotion) => (
                      <div
                        key={promotion.id}
                        className="p-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                            <Gift className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {promotion.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                                {promotion.status.toLowerCase()}
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Expires in {getExpiresIn(promotion.endDate)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700">
                    <button 
                      onClick={() => {
                        setShowPromotions(false);
                        onNavigate?.('promotions');
                      }}
                      className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300"
                    >
                      Manage promotions
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {hasSettingsAccess ? (
              <button 
                onClick={() => onNavigate?.('settings')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                aria-label="Open settings"
              >
                <Settings className="w-5 h-5 text-orange-500" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-amber-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            )}

            <button 
              onClick={() => onNavigate?.('profile')}
              className="relative flex items-center bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors cursor-pointer"
            >
              <span className="text-xs lg:text-sm font-medium text-white pl-3 lg:pl-4 pr-1.5 lg:pr-2 py-1 lg:py-1.5">
                Hello, {user?.name?.split(' ')[0] || 'User'}
              </span>
              <img
                src={user?.avatar || /*DEFAULT_AVATAR_URL*/`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random&color=fff&size=128`}
                alt={user?.name || 'User'}
                className="w-7 h-7 lg:w-8 lg:h-8 rounded-full border-2 border-white object-cover"
              />
            </button>
          </div>

          {/* MOBILE MENU BUTTON */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2 border rounded-lg"
          >
            ☰
          </button>
        </div>
      </div>

      {pendingTenant && (
        <ConfirmationModal
          title="Switch Tenant"
          message={`Switch to ${pendingTenant.name}?\n\nThis will refresh the app context and show data for the selected tenant.`}
          confirmText="Switch Tenant"
          onConfirm={confirmTenantSwitch}
          onClose={() => setPendingTenant(null)}
          icon={<Building2 className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />}
        />
      )}

      {/* MOBILE MENU */}
      {showMobileMenu && (
        <div className="absolute right-2 top-14 w-64 bg-white dark:bg-gray-900 border rounded-xl shadow-lg z-50 md:hidden">

          {canSwitchTenants && (
            <button
              onClick={() => {
                setShowTenantSwitcher(true);
                setShowMobileMenu(false)
              }}
              className="block w-full text-left px-4 py-3 border-b"
            >
              Switch Tenant
            </button>
          )}

          <button onClick={() => onNavigate?.("about")} className="block w-full text-left px-4 py-3">About</button>
          <button onClick={() => onNavigate?.("tasks")} className="block w-full text-left px-4 py-3">Tasks</button>
          <button onClick={() => onNavigate?.("help")} className="block w-full text-left px-4 py-3">Help</button>

          <button onClick={() => onNavigate?.("notifications")} className="block w-full px-4 py-3 text-left">Notifications</button>
          <button onClick={() => onNavigate?.("cart")} className="block w-full px-4 py-3 text-left">Cart</button>
          <button onClick={() => onNavigate?.("promotions")} className="block w-full px-4 py-3 text-left">Promotions</button>

          <button onClick={() => onNavigate?.("settings")} className="block w-full px-4 py-3 text-left border-t">Settings</button>
          <button onClick={() => onNavigate?.("profile")} className="block w-full px-4 py-3 text-left">Profile</button>
        </div>
      )}
    </header>
  );
}